<template>
  <div
    v-if="reviewsCount"
    class="star-rating"
    :class="{ [$class('loading')]: loading }"
  >
    <div class="star-rating__stars">
      <template v-if="stars?.filled">
        <icon-star-filled
          v-for="(_, index) of stars.filled"
          :key="`filled-${index}`"
        />
      </template>

      <template v-if="stars?.half">
        <icon-star-half
          v-for="(_, index) of stars.half"
          :key="`half-${index}`"
        />
      </template>

      <template v-if="stars?.empty">
        <icon-star-empty
          v-for="(_, index) of stars.empty"
          :key="`empty-${index}`"
        />
      </template>
    </div>

    <p
      v-if="!loading"
      class="star-rating__count text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      v-text="reviewsCountString"
    />
  </div>
</template>

<script>
/**
 * Vue: Star rating (star-rating)
 * -----------------------------------------------------------------------------
 * Shows product rating (powered by metafields).
 *
 * @param {Boolean} [loading] - Show loading state.
 * @param {Boolean|Object} [product] - Formatted product.
 *
 */
import IconStarEmpty from '~icons/general/star-empty.svg';
import IconStarFilled from '~icons/general/star-filled.svg';
import IconStarHalf from '~icons/general/star-half.svg';

export default {
  name: 'StarRating',

  components: {
    IconStarEmpty,
    IconStarFilled,
    IconStarHalf,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  computed: {
    /**
     * Compute reviews count number.
     * @returns {Number}
     */
    reviewsCount() {
      return Number(this.product.reviews?.count) || 0;
    },

    /**
     * Compute reviews count string.
     * @returns {String}
     */
    reviewsCountString() {
      return !this.product || this.loading
        ? ''
        : this.$string('product.product_card.reviews_count', {
            replace: { count: this.reviewsCount },
          });
    },

    /**
     * Compute stars.
     * @returns {Object}
     */
    stars() {
      if (!this.product || this.loading) {
        return {
          empty: 0,
          filled: 5,
          half: 0,
        };
      }

      let filled = Number(this.product.reviews?.rating);
      const half = Math.ceil(filled % 1);
      const empty = Math.floor(5 - filled);
      filled = Math.floor(filled);

      return {
        empty,
        filled,
        half,
      };
    },
  },
};
</script>

<style lang="scss">
@import './star-rating';
</style>
